import { JSX, useState, useRef, useEffect } from "react";
import profileAvatar from "../../../assets/icons/svg/setting/Add_user_icon.svg";
import WorkspaceIcon from "../../../assets/icons/svg/setting/Add_workspace _icon.svg";
import { ReactComponent as UndoIcon } from "../../../assets/icons/svg/onbording/undo-icon.svg";
import Button from "../../ui/buttons";
import { API_URLS, BASE_URL } from "../../../api/urls";
import { EnhancedTokenManager } from "../../../utils/tokenManager";
import { triggerProfileImageUpdate, useProfileImage } from "../../../contexts/ProfileImageContext";
import { triggerWorkspaceIconUpdate } from "../../../contexts/WorkspaceIconContext";
import Lottie from "lottie-react";
import DotLoader from "../../../assets/Animation/Dot Loader.json";
import { DEFAULT_FONT_FAMILY } from "../../../config/fontConfig";

interface UserProfile {
  id: number;
  email: string;
  full_name: string;
  profile_image: string | null;
  onboarded: boolean;
}

interface Workspace {
  id: number;
  name: string;
  subdomain: string;
}

// ✅ FIXED: Updated cache interface to track blob URLs
interface UserDataCache {
  userData: {
    fullName: string;
    email: string;
    workspaceName: string;
    workspaceUrl: string;
    profileImage: string | null;
    workspaceIcon: string | null;
  };
  timestamp: number;
  blobUrls: Set<string>; // Track blob URLs to prevent revoking cached ones
}

let userDataCache: UserDataCache | null = null;

const CACHE_DURATION = 15 * 60 * 1000; // 15 minutes

export const PersonalTab = (): JSX.Element => {
  const { profileImage: contextProfileImage } = useProfileImage();
  const [workspaceName, setWorkspaceName] = useState("");
  const [workspaceUrl, setWorkspaceUrl] = useState("");
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");

  // State for uploaded images
  const [uploadedWorkspaceIcon, setUploadedWorkspaceIcon] = useState<
    string | null
  >(null);
  const [uploadedProfileImage, setUploadedProfileImage] = useState<
    string | null
  >(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Store original data for comparison
  const [originalData, setOriginalData] = useState({
    fullName: "",
    email: "",
    workspaceName: "",
    workspaceUrl: "",
    profileImage: null as string | null,
    workspaceIcon: null as string | null,
  });

  // Refs for file inputs
  const workspaceIconInputRef = useRef<HTMLInputElement>(null);
  const profileImageInputRef = useRef<HTMLInputElement>(null);

  // State to track if data has been loaded
  const [dataLoaded, setDataLoaded] = useState(false);

  const [isProfileImageNew, setIsProfileImageNew] = useState(false);
  const [isWorkspaceIconNew, setIsWorkspaceIconNew] = useState(false);

  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [successMessageText, setSuccessMessageText] = useState("");
  const [successMessageType, setSuccessMessageType] = useState<
    "success" | "error"
  >("success");

  // Fetch user profile and workspace data only once or from cache
  useEffect(() => {
    // Only fetch if we haven't loaded data yet
    if (!dataLoaded) {
      // Add a small delay to ensure the component is fully mounted
      const timer = setTimeout(() => {
        loadUserData();
      }, 100);

      return () => clearTimeout(timer);
    }
  }, []);

  // ✅ FIXED: Selective cleanup - only revoke NEW user uploads, not cached blobs
  useEffect(() => {
    return () => {
      // Only revoke NEW blob URLs that user uploaded but didn't save
      // Don't revoke cached blob URLs from server
      if (isProfileImageNew && uploadedProfileImage?.startsWith("blob:")) {
        // Check if it's in cache
        if (!userDataCache?.blobUrls.has(uploadedProfileImage)) {
          URL.revokeObjectURL(uploadedProfileImage);
          console.log("🧹 Revoked new profile image blob URL");
        }
      }
      if (isWorkspaceIconNew && uploadedWorkspaceIcon?.startsWith("blob:")) {
        if (!userDataCache?.blobUrls.has(uploadedWorkspaceIcon)) {
          URL.revokeObjectURL(uploadedWorkspaceIcon);
          console.log("🧹 Revoked new workspace icon blob URL");
        }
      }
    };
  }, [
    uploadedProfileImage,
    uploadedWorkspaceIcon,
    isProfileImageNew,
    isWorkspaceIconNew,
  ]);

  const loadUserData = async () => {
    // Check if we have valid cached data
    if (
      userDataCache &&
      Date.now() - userDataCache.timestamp < CACHE_DURATION
    ) {
      console.log("✅ Loading user data from cache (blob URLs preserved)");
      const cached = userDataCache.userData;

      // Restore from cache - blob URLs are still valid
      setFullName(cached.fullName);
      setEmail(cached.email);
      setUploadedProfileImage(cached.profileImage);
      setWorkspaceName(cached.workspaceName);
      setWorkspaceUrl(cached.workspaceUrl);
      setUploadedWorkspaceIcon(cached.workspaceIcon);

      setOriginalData({
        fullName: cached.fullName,
        email: cached.email,
        workspaceName: cached.workspaceName,
        workspaceUrl: cached.workspaceUrl,
        profileImage: cached.profileImage,
        workspaceIcon: cached.workspaceIcon,
      });

      setDataLoaded(true);
      setLoading(false);
      return;
    }

    // Fetch fresh data if no valid cache
    await fetchUserData();
  };

  const fetchUserData = async () => {
    try {
      setLoading(true);
      setError(null);

      // ✅ CRITICAL FIX: Ensure token is valid before fetching
      const tokenValid = await EnhancedTokenManager.ensureValidToken();

      if (!tokenValid) {
        // ✅ Clear invalid tokens and redirect to login
        console.error("Session expired, redirecting to login");
        EnhancedTokenManager.performLogout();

        // Wait a bit before redirecting to ensure cleanup completes
        setTimeout(() => {
          window.location.href = "/";
        }, 500);

        return;
      }

      console.log("📡 Fetching user profile...");

      // ✅ Use auto-refresh fetch method instead of regular fetch
      const profileResponse = await EnhancedTokenManager.fetchWithAutoRefresh(
        API_URLS.GET_USER_PROFILE,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        },
      );

      if (!profileResponse.ok) {
        // ✅ If still failing after token refresh, session is truly invalid
        if (profileResponse.status === 401) {
          EnhancedTokenManager.performLogout();
          setTimeout(() => {
            window.location.href = "/";
          }, 500);
          return;
        }

        throw new Error(
          `Failed to fetch user profile: ${profileResponse.status}`,
        );
      }

      const userProfile: UserProfile = await profileResponse.json();
      console.log("✅ User profile fetched:", userProfile.email);

      // ✅ Fetch workspaces with auto-refresh
      const workspacesResponse =
        await EnhancedTokenManager.fetchWithAutoRefresh(
          API_URLS.GET_WORKSPACES,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
            },
          },
        );

      if (!workspacesResponse.ok) {
        throw new Error(
          `Failed to fetch workspaces: ${workspacesResponse.status}`,
        );
      }

      const workspacesData: { workspaces: Workspace[] } =
        await workspacesResponse.json();
      console.log("✅ Workspaces fetched:", workspacesData.workspaces?.length);

      const currentWorkspace = workspacesData.workspaces[0];

      // Set basic form data
      setFullName(userProfile.full_name || "");
      setEmail(userProfile.email || "");

      // ✅ Track blob URLs for cache
      const blobUrls = new Set<string>();

      // Use API profile_image, falling back to token manager cached value if API returns null
      const rawImage =
        userProfile.profile_image ||
        EnhancedTokenManager.getUserProfile()?.profile_image;

      // Handle external images (Google) without CORS issues
      let profileImageUrl: string | null = null;
      if (rawImage) {
        const profileImageSource: string = rawImage;
        try {
          const imageUrl: string = profileImageSource.startsWith("http")
            ? profileImageSource
            : `${BASE_URL}${profileImageSource.startsWith("/") ? "" : "/"}${profileImageSource}`;

          const isExternalImage = imageUrl.startsWith("http") && !imageUrl.includes(BASE_URL);

          if (isExternalImage) {
            // For external images (Google), try fetching as blob first for CSP compatibility
            try {
              const extResponse = await fetch(imageUrl, { method: "GET" });
              if (extResponse.ok) {
                const extBlob = await extResponse.blob();
                if (extBlob.size > 0) {
                  const extBlobUrl = URL.createObjectURL(extBlob);
                  profileImageUrl = extBlobUrl;
                  blobUrls.add(extBlobUrl);
                }
              }
            } catch (extError) {
              console.warn("Could not fetch external image as blob, using direct URL:", extError);
            }
            const resolvedProfileUrl: string = profileImageUrl ?? imageUrl;
            profileImageUrl = resolvedProfileUrl;
            setUploadedProfileImage(resolvedProfileUrl);
            setIsProfileImageNew(false);
          } else {
            // For internal images, fetch as blob with auth
            const imageResponse = await EnhancedTokenManager.fetchWithAutoRefresh(imageUrl, { method: "GET" });
            if (imageResponse.ok) {
              const imageBlob = await imageResponse.blob();
              const blobUrl = URL.createObjectURL(imageBlob);
              profileImageUrl = blobUrl;
              setUploadedProfileImage(blobUrl);
              blobUrls.add(blobUrl);
            }
          }
        } catch (imageError) {
          console.warn("Could not fetch profile image:", imageError);
        }
      }

      let workspaceIconUrl: string | null = null;

      if (currentWorkspace) {
        setWorkspaceName(currentWorkspace.name || "");
        setWorkspaceUrl(currentWorkspace.subdomain || "");

        // Fetch workspace icon if available
        try {
          const iconUrl = API_URLS.GET_ICON(currentWorkspace.id);
          console.log("📡 Fetching workspace icon...");

          // ✅ Use auto-refresh fetch for icon
          const iconResponse = await EnhancedTokenManager.fetchWithAutoRefresh(
            iconUrl,
            {
              method: "GET",
            },
          );

          if (iconResponse.ok) {
            const contentType = iconResponse.headers.get("content-type");

            if (contentType?.includes("application/json")) {
              const jsonData = await iconResponse.json();
              const iconUrlPath = jsonData.data?.icon_url || jsonData.icon_url;

              if (iconUrlPath) {
                const imageUrl = iconUrlPath.startsWith("http")
                  ? iconUrlPath
                  : `${BASE_URL}${iconUrlPath.startsWith("/") ? "" : "/"}${iconUrlPath}`;

                // ✅ Use auto-refresh fetch for icon image
                const imageResponse =
                  await EnhancedTokenManager.fetchWithAutoRefresh(imageUrl, {
                    method: "GET",
                  });

                if (imageResponse.ok) {
                  const imageBlob = await imageResponse.blob();
                  if (
                    imageBlob.size > 0 &&
                    imageBlob.type.startsWith("image/")
                  ) {
                    workspaceIconUrl = URL.createObjectURL(imageBlob);
                    setUploadedWorkspaceIcon(workspaceIconUrl);
                    blobUrls.add(workspaceIconUrl); // ✅ Track this blob URL
                    console.log("✅ Workspace icon loaded");
                  }
                }
              }
            } else if (contentType?.startsWith("image/")) {
              const iconBlob = await iconResponse.blob();
              if (iconBlob.size > 0) {
                workspaceIconUrl = URL.createObjectURL(iconBlob);
                setUploadedWorkspaceIcon(workspaceIconUrl);
                blobUrls.add(workspaceIconUrl); // ✅ Track this blob URL
                console.log("✅ Workspace icon loaded");
              }
            }
          }
        } catch (iconError) {
          console.warn("Could not fetch workspace icon:", iconError);
        }
      }

      // Store original data for comparison
      const originalDataObj = {
        fullName: userProfile.full_name || "",
        email: userProfile.email || "",
        workspaceName: currentWorkspace?.name || "",
        workspaceUrl: currentWorkspace?.subdomain || "",
        profileImage: profileImageUrl,
        workspaceIcon: workspaceIconUrl,
      };

      setOriginalData(originalDataObj);

      // ✅ Cache the data WITH blob URL tracking
      userDataCache = {
        userData: originalDataObj,
        timestamp: Date.now(),
        blobUrls: blobUrls, // Store blob URLs to prevent revoking
      };
      console.log("✅ User data cached with blob URL tracking");

      setDataLoaded(true);
      setError(null);
    } catch (err) {
      console.error("❌ Error fetching user data:", err);
      const errorMessage =
        err instanceof Error ? err.message : "Failed to load user data";
      setError(errorMessage);
      setDataLoaded(false);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setFullName(originalData.fullName);
    setEmail(originalData.email);
    setWorkspaceName(originalData.workspaceName);
    setWorkspaceUrl(originalData.workspaceUrl);
    setUploadedProfileImage(originalData.profileImage);
    setUploadedWorkspaceIcon(originalData.workspaceIcon);
    setIsProfileImageNew(false);
    setIsWorkspaceIconNew(false);
    setError(null);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);

      const accessToken = EnhancedTokenManager.getAccessToken();
      if (!accessToken) {
        setError("No access token available. Please sign in again.");
        return;
      }

      const userProfile = EnhancedTokenManager.getUserProfile();
      if (!userProfile || !userProfile.id) {
        setError("User profile not found. Please try refreshing the page.");
        return;
      }

      const workspaceId = EnhancedTokenManager.getWorkspaceIdForCurrentUser();
      const formData = new FormData();

      if (workspaceId) {
        formData.append("workspace_id", workspaceId.toString());
        console.log("Workspace ID added to FormData:", workspaceId);
      }

      if (fullName !== originalData.fullName && fullName.trim()) {
        formData.append("full_name", fullName.trim());
      }

      if (workspaceUrl !== originalData.workspaceUrl && workspaceUrl.trim()) {
        formData.append("workspace_url", workspaceUrl.trim());
      }

      const isUpdatingWorkspaceIcon =
        isWorkspaceIconNew && uploadedWorkspaceIcon;

      if (isProfileImageNew && uploadedProfileImage) {
        if (uploadedProfileImage.startsWith("data:")) {
          const blob = await fetch(uploadedProfileImage).then((res) =>
            res.blob(),
          );
          const fileExtension = blob.type.split("/")[1] || "jpg";
          formData.append(
            "profile_image",
            blob,
            `profile_image.${fileExtension}`,
          );
          console.log(
            "✅ Profile image added to FormData:",
            blob.type,
            blob.size,
            "bytes",
          );
        }
      }

      if (isUpdatingWorkspaceIcon) {
        if (uploadedWorkspaceIcon.startsWith("data:")) {
          const blob = await fetch(uploadedWorkspaceIcon).then((res) =>
            res.blob(),
          );
          const fileExtension = blob.type.split("/")[1] || "jpg";
          const fileName = `icon_url_${Date.now()}.${fileExtension}`;
          formData.append("icon_url", blob, fileName);
          console.log(
            "✅ Workspace icon added to FormData:",
            blob.type,
            blob.size,
            "bytes",
          );
        }
      }

      let response = await fetch(
        API_URLS.UPDATE_PROFILE_AND_WORKSPACE_URL,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
          body: formData,
        },
      );

      if (response.status === 405) {
        console.log("POST not allowed, trying PATCH method...");
        response = await fetch(
          API_URLS.UPDATE_PROFILE_AND_WORKSPACE_URL,
          {
            method: "PATCH",
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
            body: formData,
          },
        );
      }

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(
          `Failed to update profile: ${response.status} - ${errorText}`,
        );
      }

      const result = await response.json();
      console.log("=== API Response ===");
      console.log("Full response:", JSON.stringify(result, null, 2));

      // ✅ Track new blob URLs after save
      const newBlobUrls = new Set<string>(userDataCache?.blobUrls || []);

      // Fetch updated images as blobs
      let updatedProfileImage = uploadedProfileImage;
      if (result.data?.user?.profile_image) {
        try {
          const imageUrl = result.data.user.profile_image.startsWith("http")
            ? result.data.user.profile_image
            : `${BASE_URL}${result.data.user.profile_image.startsWith("/") ? "" : "/"}${result.data.user.profile_image}`;

          const imageResponse = await fetch(imageUrl, {
            method: "GET",
            headers: { Authorization: `Bearer ${accessToken}` },
          });

          if (imageResponse.ok) {
            const imageBlob = await imageResponse.blob();
            // Revoke old blob if it's new (not cached)
            if (
              updatedProfileImage?.startsWith("blob:") &&
              !newBlobUrls.has(updatedProfileImage)
            ) {
              URL.revokeObjectURL(updatedProfileImage);
            }
            updatedProfileImage = URL.createObjectURL(imageBlob);
            newBlobUrls.add(updatedProfileImage); // Track new blob
            console.log("✅ Updated profile image fetched as blob");
          }
        } catch (error) {
          console.warn("Could not fetch updated profile image:", error);
        }
      }

      let updatedWorkspaceIcon = uploadedWorkspaceIcon;
      if (workspaceId) {
        try {
          const iconUrl = `${API_URLS.GET_ICON(workspaceId)}?t=${Date.now()}`;
          console.log("Fetching updated workspace icon from:", iconUrl);

          const iconResponse = await fetch(iconUrl, {
            method: "GET",
            headers: { Authorization: `Bearer ${accessToken}` },
            cache: "no-store",
          });

          if (iconResponse.ok) {
            const contentType = iconResponse.headers.get("content-type");

            if (contentType?.includes("application/json")) {
              const jsonData = await iconResponse.json();
              const iconUrlPath = jsonData.data?.icon_url || jsonData.icon_url;

              if (iconUrlPath) {
                const imageUrl = iconUrlPath.startsWith("http")
                  ? iconUrlPath
                  : `${BASE_URL}${iconUrlPath.startsWith("/") ? "" : "/"}${iconUrlPath}`;

                const finalImageUrl = `${imageUrl}${imageUrl.includes("?") ? "&" : "?"}t=${Date.now()}`;

                const imageResponse = await fetch(finalImageUrl, {
                  method: "GET",
                  headers: { Authorization: `Bearer ${accessToken}` },
                  cache: "no-store",
                });

                if (imageResponse.ok) {
                  const imageBlob = await imageResponse.blob();

                  if (
                    imageBlob.size > 0 &&
                    imageBlob.type.startsWith("image/")
                  ) {
                    // Revoke old blob if it's new (not cached)
                    if (
                      updatedWorkspaceIcon?.startsWith("blob:") &&
                      !newBlobUrls.has(updatedWorkspaceIcon)
                    ) {
                      URL.revokeObjectURL(updatedWorkspaceIcon);
                    }
                    updatedWorkspaceIcon = URL.createObjectURL(imageBlob);
                    newBlobUrls.add(updatedWorkspaceIcon); // Track new blob
                    console.log("✅ Updated workspace icon fetched as blob");
                  }
                }
              }
            } else if (contentType?.startsWith("image/")) {
              const iconBlob = await iconResponse.blob();

              if (iconBlob.size > 0 && iconBlob.type.startsWith("image/")) {
                if (
                  updatedWorkspaceIcon?.startsWith("blob:") &&
                  !newBlobUrls.has(updatedWorkspaceIcon)
                ) {
                  URL.revokeObjectURL(updatedWorkspaceIcon);
                }
                updatedWorkspaceIcon = URL.createObjectURL(iconBlob);
                newBlobUrls.add(updatedWorkspaceIcon); // Track new blob
                console.log("✅ Updated workspace icon fetched as blob");
              }
            }
          }
        } catch (error) {
          console.warn("Could not fetch updated workspace icon:", error);
        }
      }

      // Update state and cache
      const updatedData = {
        fullName: result.data?.user?.full_name || fullName,
        email,
        workspaceName,
        workspaceUrl,
        profileImage: updatedProfileImage,
        workspaceIcon: updatedWorkspaceIcon,
      };

      setOriginalData(updatedData);
      setUploadedProfileImage(updatedProfileImage);
      setUploadedWorkspaceIcon(updatedWorkspaceIcon);
      setIsProfileImageNew(false);
      setIsWorkspaceIconNew(false);

      // ✅ Update cache with new blob URLs
      userDataCache = {
        userData: updatedData,
        timestamp: Date.now(),
        blobUrls: newBlobUrls,
      };

      // Update token manager
      if (userProfile) {
        const updatedProfile = {
          ...userProfile,
          full_name: result.data?.user?.full_name || fullName,
          profile_image:
            result.data?.user?.profile_image || userProfile.profile_image,
        };

        if (result.data?.workspace?.icon_url && workspaceId) {
          const workspaceData =
            EnhancedTokenManager.getWorkspaceDataForCurrentUser();
          if (workspaceData) {
            EnhancedTokenManager.setWorkspaceDataForUser(userProfile.id, {
              ...workspaceData,
              icon_url: result.data.workspace.icon_url,
            });
          }
          EnhancedTokenManager.setWorkspaceIconForUser(
            userProfile.id,
            result.data.workspace.icon_url,
          );
        }

        EnhancedTokenManager.setUserProfile(updatedProfile);

        if (isProfileImageNew && updatedProfileImage) {
          console.log(
            "⚡ Triggering IMMEDIATE profile image update with blob URL:",
            updatedProfileImage,
          );
          triggerProfileImageUpdate(updatedProfileImage);
        } else {
          triggerProfileImageUpdate();
          console.log("✅ Profile image update triggered (API fetch)");
        }
      }

      if (isUpdatingWorkspaceIcon && updatedWorkspaceIcon) {
        console.log(
          "⚡ Triggering IMMEDIATE workspace icon update with blob URL:",
          updatedWorkspaceIcon,
        );
        triggerWorkspaceIconUpdate(updatedWorkspaceIcon);
      }

      const successMessage = result.message || "Changes saved successfully!";
      setSuccessMessageText(successMessage);
      setSuccessMessageType("success");
      setShowSuccessMessage(true);
      setTimeout(() => setShowSuccessMessage(false), 3000);
    } catch (err) {
      console.error("Error saving changes:", err);
      const errorMessage =
        err instanceof Error ? err.message : "Failed to save changes";
      setSuccessMessageText(errorMessage);
      setSuccessMessageType("error");
      setShowSuccessMessage(true);
      setTimeout(() => setShowSuccessMessage(false), 3000);
    } finally {
      setSaving(false);
    }
  };

  const hasChanges =
    fullName !== originalData.fullName ||
    workspaceUrl !== originalData.workspaceUrl ||
    isProfileImageNew ||
    isWorkspaceIconNew;

  const isSaveDisabled = !hasChanges || saving;

  const convertFileToUrl = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const result = e.target?.result as string;
        resolve(result);
      };
      reader.onerror = () => reject(new Error("Failed to read file"));
      reader.readAsDataURL(file);
    });
  };

  const handleWorkspaceIconUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (file) {
      const allowedFormats = ["image/jpeg", "image/jpg", "image/png"];
      if (!allowedFormats.includes(file.type.toLowerCase())) {
        setSuccessMessageText(
          "Only JPG, PNG, and JPEG formats are allowed for workspace icon",
        );
        setSuccessMessageType("error");
        setShowSuccessMessage(true);
        setTimeout(() => setShowSuccessMessage(false), 3000);
        if (workspaceIconInputRef.current) {
          workspaceIconInputRef.current.value = "";
        }
        return;
      }

      if (file.size > 800 * 1024) {
        setSuccessMessageText("Image file size should be less than 800KB");
        setSuccessMessageType("error");
        setShowSuccessMessage(true);
        setTimeout(() => setShowSuccessMessage(false), 3000);
        if (workspaceIconInputRef.current) {
          workspaceIconInputRef.current.value = "";
        }
        return;
      }

      try {
        const fileUrl = await convertFileToUrl(file);
        setUploadedWorkspaceIcon(fileUrl);
        setIsWorkspaceIconNew(true);
        setError(null);
      } catch (error) {
        console.error("Error processing file:", error);
        setSuccessMessageText("Error processing the uploaded file");
        setSuccessMessageType("error");
        setShowSuccessMessage(true);
        setTimeout(() => setShowSuccessMessage(false), 3000);
      }
    }
  };

  const handleProfileImageUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (file) {
      const allowedFormats = ["image/jpeg", "image/jpg", "image/png"];
      if (!allowedFormats.includes(file.type.toLowerCase())) {
        setSuccessMessageText(
          "Only JPG, PNG, and JPEG formats are allowed for profile picture",
        );
        setSuccessMessageType("error");
        setShowSuccessMessage(true);
        setTimeout(() => setShowSuccessMessage(false), 3000);
        if (profileImageInputRef.current) {
          profileImageInputRef.current.value = "";
        }
        return;
      }

      if (file.size > 800 * 1024) {
        setSuccessMessageText("Image file size should be less than 800KB");
        setSuccessMessageType("error");
        setShowSuccessMessage(true);
        setTimeout(() => setShowSuccessMessage(false), 3000);
        if (profileImageInputRef.current) {
          profileImageInputRef.current.value = "";
        }
        return;
      }

      try {
        const fileUrl = await convertFileToUrl(file);
        setUploadedProfileImage(fileUrl);
        setIsProfileImageNew(true);
        setError(null);
      } catch (error) {
        console.error("Error processing file:", error);
        setSuccessMessageText("Error processing the uploaded file");
        setSuccessMessageType("error");
        setShowSuccessMessage(true);
        setTimeout(() => setShowSuccessMessage(false), 3000);
      }
    }
  };

  const handleWorkspaceIconClick = () => {
    workspaceIconInputRef.current?.click();
  };

  const handleProfileImageClick = () => {
    profileImageInputRef.current?.click();
  };

  const handleWorkspaceIconReplace = (e: React.MouseEvent) => {
    e.stopPropagation();
    workspaceIconInputRef.current?.click();
  };

  const handleProfileImageReplace = (e: React.MouseEvent) => {
    e.stopPropagation();
    profileImageInputRef.current?.click();
  };

  return (
    <>
      {/* Success/Error Message Notification */}
      {showSuccessMessage && (
        <div className="fixed top-4 right-4 z-50 max-w-md">
          <div
            className={`${
              successMessageType === "success"
                ? "bg-green-50 border-green-200"
                : "bg-red-50 border-red-200"
            } border rounded-lg p-4 shadow-lg`}
          >
            <div className="flex items-start">
              <div className="flex-shrink-0">
                {successMessageType === "success" ? (
                  <svg
                    className="h-5 w-5 text-green-400"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                ) : (
                  <svg
                    className="h-5 w-5 text-red-400"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                      clipRule="evenodd"
                    />
                  </svg>
                )}
              </div>
              <div className="ml-3 flex-1">
                <p
                  className={`text-sm font-medium ${
                    successMessageType === "success"
                      ? "text-green-800"
                      : "text-red-800"
                  }`}
                  style={{ fontFamily: DEFAULT_FONT_FAMILY }}
                >
                  {successMessageType === "success" ? "Success!" : "Error"}
                </p>
                <p
                  className={`text-sm mt-1 ${
                    successMessageType === "success"
                      ? "text-green-700"
                      : "text-red-700"
                  }`}
                  style={{ fontFamily: DEFAULT_FONT_FAMILY }}
                >
                  {successMessageText}
                </p>
              </div>
              <div className="ml-4 flex-shrink-0">
                <button
                  onClick={() => setShowSuccessMessage(false)}
                  className={`${
                    successMessageType === "success"
                      ? "bg-green-50 text-green-400 hover:text-green-500"
                      : "bg-red-50 text-red-400 hover:text-red-500"
                  } rounded-md inline-flex focus:outline-none`}
                >
                  <span
                    className="sr-only"
                    style={{ fontFamily: DEFAULT_FONT_FAMILY }}
                  >
                    Close
                  </span>
                  <svg
                    className="h-5 w-5"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="">
        {/* Loading State */}
        {loading && (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <Lottie
                animationData={DotLoader}
                loop={true}
                style={{ width: 120, height: 48 }}
              />
              <p
                className="text-gray-500 mt-2"
                style={{ fontFamily: DEFAULT_FONT_FAMILY }}
              >
                Loading your profile...
              </p>
            </div>
          </div>
        )}

        {/* Error Display with Retry Button */}
        {error && !loading && (
          <div className="mb-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-start">
                <svg
                  className="h-5 w-5 text-red-400 mt-0.5"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                  />
                </svg>
                <div className="ml-3 flex-1">
                  <h3
                    className="text-sm font-medium text-red-800"
                    style={{ fontFamily: DEFAULT_FONT_FAMILY }}
                  >
                    Failed to load profile
                  </h3>
                  <p
                    className="text-red-600 text-sm mt-1"
                    style={{ fontFamily: DEFAULT_FONT_FAMILY }}
                  >
                    {error}
                  </p>
                  <button
                    onClick={() => {
                      setError(null);
                      setDataLoaded(false);
                      loadUserData();
                    }}
                    className="mt-2 text-sm font-medium text-red-600 hover:text-red-500 underline"
                    style={{ fontFamily: DEFAULT_FONT_FAMILY }}
                  >
                    Try again
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Main Content - Only show when not loading and no error */}
        {!loading && !error && (
          <div className="flex flex-col gap-6">
            {/* Personal Details Row */}
            <div className="flex flex-row">
              {/* Personal Details */}
              <div className="w-full h-full bg-[#FCFDFE] border border-[#CBCED9] rounded-xl px-6 py-4">
                <p
                  className="font-semibold text-lg text-[#2A3547]"
                  style={{ fontFamily: DEFAULT_FONT_FAMILY }}
                >
                  Personal details
                </p>

                <div className="mt-3">
                  <label
                    className="font-semibold text-sm text-[#2A3547]"
                    style={{ fontFamily: DEFAULT_FONT_FAMILY }}
                  >
                    Full name
                  </label>
                  <input
                    className="text-base font-normal h-[40px] font-inter w-full border border-[#898D9C] rounded-lg pl-[18px] mt-2 focus:outline-none focus:ring-1 focus:ring-[#7ABFFF] focus:border-transparent"
                    type="text"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                  
                  />
                </div>

                <div className="mt-2">
                  <label
                    className="font-semibold text-sm text-[#2A3547]"
                    style={{ fontFamily: DEFAULT_FONT_FAMILY }}
                  >
                    Email address
                  </label>
                  <input
                    className="text-base text-[#778592] font-normal h-[40px] w-full font-inter  rounded-lg pl-[18px] mt-2 focus:outline-none focus:ring-1 focus:ring-[#7ABFFF] focus:border-transparent bg-[#ECF1F6]"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled
                    title="Email cannot be changed"
                    
                  />
                </div>
              </div>

              <div className="p-3" />

              {/* Change Profile Image */}
              <div className="w-full bg-[#FCFDFE] border border-[#CBCED9] rounded-xl px-6 py-6 flex flex-col">
                <p
                  className="font-semibold text-lg text-[#2A3547]"
                  style={{ fontFamily: DEFAULT_FONT_FAMILY }}
                >
                  Change profile
                </p>
                <div className="flex flex-col items-center justify-center gap-5 flex-grow mt-4">
                  <div
                    className="relative w-20 h-20 rounded-full flex items-center justify-center cursor-pointer group"
                    onClick={handleProfileImageClick}
                  >
                    <img
                      className="h-20 w-20 rounded-full object-cover"
                      src={
                        isProfileImageNew
                          ? (uploadedProfileImage || profileAvatar)
                          : (contextProfileImage || profileAvatar)
                      }
                      alt="profile avatar"
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = profileAvatar;
                      }}
                    />

                    {uploadedProfileImage && (
                      <div
                        className="absolute inset-0 bg-black bg-opacity-50 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                        onClick={handleProfileImageReplace}
                      >
                        <UndoIcon className="w-6 h-6 text-white" />
                      </div>
                    )}
                  </div>

                  <p
                    className="text-[#2A354799] text-sm text-center tracking-[-0.28px] font-normal"
                    style={{ fontFamily: DEFAULT_FONT_FAMILY }}
                  >
                    Allowed JPG, PNG, or JPEG. Max size of 800KB
                  </p>

                  <input
                    ref={profileImageInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleProfileImageUpload}
                    className="hidden"
                  />
                </div>
              </div>
            </div>

            {/* Workspace Row */}
            <div className="flex flex-row">
              {/* Workspace Details */}
              <div className="w-full h-full bg-[#FCFDFE] border border-[#CBCED9] rounded-xl px-6 py-4">
                <p
                  className="font-semibold text-lg text-[#2A3547]"
                  style={{ fontFamily: DEFAULT_FONT_FAMILY }}
                >
                  Workspace details
                </p>

                <div className="mt-3">
                  <label
                    className="font-semibold text-sm text-[#2A3547]"
                    style={{ fontFamily: DEFAULT_FONT_FAMILY }}
                  >
                    Workspace name
                  </label>
                  <input
                   className="text-base text-[#778592] font-normal h-[40px] w-full font-inter  rounded-lg pl-[18px] mt-2 focus:outline-none focus:ring-1 focus:ring-[#7ABFFF] focus:border-transparent bg-[#ECF1F6]"
                    type="text"
                    value={workspaceName}
                    onChange={(e) => setWorkspaceName(e.target.value)}
                    disabled
                    title="Workspace name cannot be changed"
                  
                  />
                </div>

                <div className="mt-2">
                  <label
                    className="font-semibold text-sm text-[#2A3547]"
                    style={{ fontFamily: DEFAULT_FONT_FAMILY }}
                  >
                    Workspace URL
                  </label>
                  <input
                    className="text-base text-[#778592] font-normal h-[40px] w-full font-inter  rounded-lg pl-[18px] mt-2 focus:outline-none focus:ring-1 focus:ring-[#7ABFFF] focus:border-transparent bg-[#ECF1F6]"
                    type="text"
                    value={workspaceUrl}
                    onChange={(e) => setWorkspaceUrl(e.target.value)}
                    disabled
                    title="Workspace url cannot be changed"
                    
                  />
                </div>
              </div>

              <div className="p-3" />

              {/* Change Workspace Icon */}
              <div className="w-full bg-[#FCFDFE] border border-[#CBCED9] rounded-xl px-6 py-6 flex flex-col">
                <p
                  className="font-semibold text-lg text-[#2A3547]"
                  style={{ fontFamily: DEFAULT_FONT_FAMILY }}
                >
                  Change workspace icon for "{workspaceName || "OpenUI"}"
                </p>
                <div className="flex flex-col items-center justify-center gap-5 flex-grow mt-4">
                  <div
                    className="relative w-20 h-20 rounded-xl flex items-center justify-center cursor-pointer group"
                    onClick={handleWorkspaceIconClick}
                  >
                    <img
                      className="h-20 w-20 object-cover rounded-xl"
                      src={uploadedWorkspaceIcon || WorkspaceIcon}
                      alt="workspace avatar"
                    />

                    {uploadedWorkspaceIcon && (
                      <div
                        className="absolute inset-0 bg-black bg-opacity-50 rounded-xl flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                        onClick={handleWorkspaceIconReplace}
                      >
                        <UndoIcon className="w-6 h-6 text-white" />
                      </div>
                    )}
                  </div>

                  <p
                    className="text-[#2A354799] text-sm text-center tracking-[-0.28px] font-normal"
                    style={{ fontFamily: DEFAULT_FONT_FAMILY }}
                  >
                    Allowed JPG, PNG, or JPEG. Max size of 800KB
                  </p>

                  <input
                    ref={workspaceIconInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleWorkspaceIconUpload}
                    className="hidden"
                  />
                </div>
              </div>
            </div>
          </div>
        )}
        {/* Footer Buttons */}
        <div className="flex flex-row justify-end border-t border-[#E9ECF5] mt-10 pt-[20px] pb-[14px]">
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={!hasChanges}
            className="w-[141px] h-[44px] ml-4"
            style={{ fontFamily: DEFAULT_FONT_FAMILY }}
          >
            {saving ? (
              <span
                className="inline-block relative"
                style={{ width: 40, height: 16 }}
              >
                <Lottie
                  animationData={DotLoader}
                  loop={true}
                  style={{
                    width: 120,
                    height: 48,
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                  }}
                />
              </span>
            ) : (
              "Update"
            )}
          </Button>
        </div>
      </div>
    </>
  );
};

export default PersonalTab;
